/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Pivot, PivotItem, PivotLinkSize } from '@fluentui/react/lib/Pivot';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { AuthoringResourcePivot } from 'src/luis/appShell/managePane/azureAccountsPage/components/AuthoringResourcePivot';
import { PredictionResourcesPivot } from 'src/luis/appShell/managePane/azureAccountsPage/components/PredictionResourcesPivot';

const messages = defineMessages({
    pageTitle: {
        id: 'luis.azureCsAccountsPage.title',
        defaultMessage: 'Azure Resources'
    },
    instructions: {
        id: 'luis.azureCsAccountsPage.instructions',
        defaultMessage: `LUIS uses two types of resources: authoring and prediction. The authoring key is needed for authoring,
publishing, managing collaborators, and versioning. An authoring resource is created for you when you create your
azure cognitive service account. When you are ready to publish your LUIS app, you need to create the prediction resource
key and use the prediction key with endpoint queries.`
    },
    predictionResources: {
        id: 'luis.azureCsAccountsPage.predictionResources',
        defaultMessage: 'Prediction Resources'
    },
    authoringResource: {
        id: 'luis.azureCsAccountsPage.authoringResource',
        defaultMessage: 'Authoring Resource'
    },
    learnMoreDetailed: {
        id: 'luis.azureCsAccountsPage.learnMoreDetailed',
        defaultMessage: 'Learn more about authoring resources.'
    }
});

const { styleComponent } = getStylist('AzureCsAccountsPage');
const Instructions = styleComponent<LuisTextProps>(LuisText)('Instructions', theme => ({ maxWidth: theme.instructionsMaxWidth }));

const assignResourcesLearnMoreUrl =
    'https://docs.microsoft.com/azure/cognitive-services/luis/luis-how-to-azure-subscription#assign-a-resource-to-an-app';

/**
 * The component displaying the azure resources
 * of a LUIS app.
 */
export const AzureCsAccountsPage = observer(() => {
    const { localize } = useLocalization();

    return (
        <Page size="container-fluid" pageName="AzureCsAccountsPage" title={localize(messages.pageTitle)}>
            <EditableTitle title={localize(messages.pageTitle)} />

            <Stack tokens={{ childrenGap: 20 }}>
                <Instructions lineHeight="1.5">
                    {localize(messages.instructions)}
                    &nbsp;
                    <ExternalLink aria-label={localize(messages.learnMoreDetailed)} href={assignResourcesLearnMoreUrl}>
                        {localize(globalMessages.learnMore)}
                    </ExternalLink>
                </Instructions>

                <Pivot linkSize={PivotLinkSize.large}>
                    <PivotItem headerText={localize(messages.predictionResources)}>
                        <PredictionResourcesPivot />
                    </PivotItem>

                    <PivotItem headerText={localize(messages.authoringResource)}>
                        <AuthoringResourcePivot />
                    </PivotItem>
                </Pivot>
            </Stack>
        </Page>
    );
});
